import React from "react";
import { liveKey, oddsHoldText } from "./useInPlayOdds";

const oddsSize = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : "";
};

/**
 * Returns true when `price` falls within [limit.min, limit.max].
 * Mirrors the MultiMarket check: oddsLimit.min <= price && oddsLimit.max >= price
 */
const inOddsRange = (limit, price) => {
  const p = Number(price);
  if (!Number.isFinite(p) || p <= 0) return false;
  if (!limit || limit.min == null || limit.max == null) return true;
  return Number(limit.min) <= p && Number(limit.max) >= p;
};

/**
 * "Match Odds" block for one match in the Sports list.
 *
 * Renders identically to the t1 section in MultiMarket:
 *  - suspend / ball-running overlay (`overlay-match`)
 *  - out-of-range cells get `single-disabled` + inner `<span className="disabled">`
 *  - maxPrice exceeded → `maxprice-exceeded` grey overlay over both cells
 *  - `back-active` / `lay-active` for the active bet
 *  - `spark-back` / `spark-lay` for price-change flash
 *  - `pointerEvents: none` when suspended or out-of-range
 *
 * Props
 *  item          – the match object from pageData
 *  sportType     – "cricket" | "soccer" | "tennis" …
 *  liveEnabled   – true on the In-Play tab; false for Today/Tomorrow
 *  liveOdds      – map keyed by `${gameId}_${marketId}`
 *  oddsFlash     – map keyed by `${liveKey}-${sId}:back` / `:lay`
 *  clickedBet    – currently open bet runner object
 *  clickedTableData – "b1" | "l1" | ""
 *  onBet(e, runner, side, item, sportType)
 */
const InPlayOddsTable = ({
  item,
  sportType,
  liveEnabled,
  liveOdds,
  oddsFlash,
  clickedBet,
  clickedTableData,
  onBet,
}) => {
  const key = liveKey(item);
  const live = liveEnabled ? liveOdds[key] : undefined;

  // --- build fallback rows from the REST snapshot --------------------------
  const [team1 = "", team2 = ""] = (item?.eventName || "").split(" v ");
  const fallbackRows = [
    {
      sId: `${item.marketId}_1`,
      sortPriority: 1,
      nat: team1,
      b1: item.back1,
      bs1: item.backSize1,
      l1: item.lay1,
      ls1: item.laySize1,
      status: item.status || "ACTIVE",
    },
    {
      sId: `${item.marketId}_3`,
      sortPriority: 3,
      nat: team2,
      b1: item.back3,
      bs1: item.backSize3,
      l1: item.lay3,
      ls1: item.laySize3,
      status: item.status || "ACTIVE",
    },
  ];
  const isThreeWay =
    item?.type === "soccer" || Number(item?.RunnersNumber) > 2 ||
    (Array.isArray(item?.t1) && item.t1.length >= 3);
  if (isThreeWay) {
    fallbackRows.splice(1, 0, {
      sId: `${item.marketId}_2`,
      sortPriority: 2,
      nat: "The Draw",
      b1: item.back2,
      bs1: item.backSize2,
      l1: item.lay2,
      ls1: item.laySize2,
      status: item.status || "ACTIVE",
    });
  }

  // Use live socket t1 if available; otherwise fall back to REST snapshot t1 or flat fields
  const rows =
    live && Array.isArray(live.t1) && live.t1.length
      ? live.t1
      : Array.isArray(item?.t1) && item.t1.length
        ? item.t1
        : fallbackRows;

  const suspendFlag = live
    ? live.suspended === true || live.suspend?.odds === true
    : item?.suspend?.odds === true;
  const anyHold = rows.some((r) => oddsHoldText(r?.status));
  const hasPrices = rows.some((r) => Number(r?.b1) > 0 || Number(r?.l1) > 0);
  if (!hasPrices && !anyHold && !suspendFlag) return null;

  // --- limits (mirrors MultiMarket) ----------------------------------------
  // oddsLimit  → disables individual price when out of [min, max]
  // bet_odds_limit.maxPrice → greys entire pair when either price exceeds it
  const oddsLimit = live?.oddsLimit ?? item?.oddsLimit ?? null;
  const betOddsLimit = live?.bet_odds_limit ?? item?.bet_odds_limit ?? null;
  const maxPrice = Number(betOddsLimit?.maxPrice) || 0;

  // totalmatch shown in the header
  const matched = rows[0]?.totalmatch;

  return (
    <div className="sports-team-table" onClick={(e) => e.stopPropagation()}>
      <div className="match-odd-table bg-white px-0 pt-1">
        {/* Header */}
        <div className="d-flex back-lay-head justify-content-end ms-auto pb-1 bg-yellow w-100">
          <div
            className="half-table"
            style={{ color: "var(--colorSecond)", borderRight: "1px solid var(--colorSecond)" }}
          >
            <h4 className="text-left">Match Odds</h4>
            {matched && (
              <span style={{ fontSize: "10.4px", paddingLeft: "0.5rem" }}>
                Matched <span style={{ fontWeight: 700 }}>{matched}</span>
              </span>
            )}
          </div>
          <span className="text-center half-table-text" style={{ width: "64px" }}>Back</span>
          <span className="text-center half-table-text" style={{ width: "64px" }}>Lay</span>
        </div>

        {/* Runner rows */}
        <ul className="mb-1 rounded position-relative p-0">
          {rows.map((runner, idx) => {
            const flashId = `${key}-${runner?.sId ?? idx}`;




            // Whole-market suspend covers every runner, the same way MultiMarket
            // rewrites the book. Otherwise the feed status (Suspend / Ball Running)
            // covers that runner only.
            const hold = suspendFlag ? "Suspended" : oddsHoldText(runner?.status);

            // const hold = oddsHoldText("SUSPEND");

            // ── out-of-range (same as MultiMarket oddsLimit check) ──────────
            const backInRange = inOddsRange(oddsLimit, runner?.b1);
            const layInRange = inOddsRange(oddsLimit, runner?.l1);

            // ── maxPrice exceeded ───────────────────────────────────────────
            const backOverMax =
              !hold && maxPrice > 0 && Number(runner?.b1) > maxPrice;
            const layOverMax =
              !hold && maxPrice > 0 && Number(runner?.l1) > maxPrice;
            const maxExceeded = backOverMax || layOverMax;

            // ── disabled state (price out of range or suspended) ────────────
            const backDisabled = Boolean(hold) || !backInRange;
            const layDisabled = Boolean(hold) || !layInRange;

            // ── active (clicked) state ──────────────────────────────────────
            const isBackActive =
              clickedBet?.sId === runner?.sId && clickedTableData === "b1";
            const isLayActive =
              clickedBet?.sId === runner?.sId && clickedTableData === "l1";

            return (
              <li key={runner?.sId ?? idx} style={{ padding: 0 }}>
                {/* Runner name */}
                <h2 style={{ paddingLeft: ".625rem", paddingRight: ".625rem" }}>
                  {runner?.nat}
                </h2>

                {/* Price cells */}
                <div className="lay-back-table" style={{ position: "relative", gap: "3px" }}>
                  {/* Back cell */}
                  <div
                    id={`back_odds_${idx}`}
                    className={[
                      isBackActive ? "back-active" : "",
                      "back",
                      oddsFlash[`${flashId}:back`] ? "spark-back" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={(e) => onBet(e, runner, "b1", item, sportType)}
                    style={{ pointerEvents: backDisabled ? "none" : "auto", marginRight: 0 }}
                  >
                    <span className={backDisabled ? "single-disabled" : ""}>
                      {backDisabled && <span className="disabled"> </span>}
                      <strong>{runner?.b1}</strong>
                      <span className="d-block">{oddsSize(runner?.bs1)}</span>
                    </span>
                  </div>

                  {/* Lay cell */}
                  <div
                    id={`lay_odds_${idx}`}
                    className={[
                      isLayActive ? "lay-active" : "",
                      "lay",
                      oddsFlash[`${flashId}:lay`] ? "spark-lay" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={(e) => onBet(e, runner, "l1", item, sportType)}
                    style={{ pointerEvents: layDisabled ? "none" : "auto", marginRight: 0 }}
                  >
                    <span className={layDisabled ? "single-disabled" : ""}>
                      {layDisabled && <span className="disabled"> </span>}
                      <strong>{runner?.l1}</strong>
                      <span className="d-block">{oddsSize(runner?.ls1)}</span>
                    </span>
                  </div>

                  {/* maxPrice exceeded overlay (grey, non-interactive) */}
                  {live && maxExceeded && !hold && (
                    <div
                      className="maxprice-exceeded"
                      aria-label="Maximum odds price exceeded"
                      title="Maximum odds price exceeded"
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 5,
                        background: "#6f8898",
                        opacity: 0.8,
                        cursor: "not-allowed",
                        width: "100%",
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {/* Suspend / Ball-Running overlay (same gray cover as MultiMarket) */}
                  {hold && (
                    <div
                      className="overlay-match"
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                        width: "auto",
                        height: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#6f8898",
                        color: "#fff",
                        opacity: 0.8,
                        borderRadius: 6,
                        zIndex: 6,
                        fontWeight: 600,
                        pointerEvents: "none",
                      }}
                    >
                      {hold}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {/* Min/Max footer (mirrors MultiMarket) */}
        {betOddsLimit && (
          <div className="market-depth-sec d-flex justify-content-end pt-1 justify-content-between items-content-center">
            <div className="d-flex align-items-center min-max">
              <svg
                stroke="currentColor"
                fill="currentColor"
                strokeWidth="0"
                viewBox="0 0 256 512"
                height="14px"
                width="14px"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M214.059 377.941H168V134.059h46.059c21.382 0 32.09-25.851 16.971-40.971L144.971 7.029c-9.373-9.373-24.568-9.373-33.941 0L24.971 93.088c-15.119 15.119-4.411 40.971 16.971 40.971H88v243.882H41.941c-21.382 0-32.09 25.851-16.971 40.971l86.059 86.059c9.373 9.373 24.568 9.373 33.941 0l86.059-86.059c15.12-15.119 4.412-40.971-16.97-40.971z" />
              </svg>
              <span style={{ fontWeight: "600" }}>Min/Max</span>
              <strong style={{ fontWeight: "600" }}>
                {betOddsLimit.min}/{betOddsLimit.max}
              </strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InPlayOddsTable;
